import { useCallback } from 'react';
import { NavigateOptions, useNavigate } from 'react-router-dom';
import { SPA_PATHS_OUTSIDE_V2 } from '@features/auth/constants';

const isSpaPath = (path: string): boolean => {
  const pathname = path.split(/[?#]/)[0];

  return pathname === '/v2' || pathname.startsWith('/v2/') || SPA_PATHS_OUTSIDE_V2.includes(pathname);
};

/**
 * Hook for navigation between SPA and Drupal.
 *
 * Automatically determines redirect method based on the target path:
 * - If the pathname is under '/v2' or is an auth URL the SPA serves outside /v2 → uses React Router (SPA navigation)
 * - Otherwise → uses window.location.href (full page redirect to Drupal)
 *
 * @example
 * const redirect = useRedirect();
 *
 * redirect('/');                  // → Drupal (full page redirect)
 * redirect('/v2/dashboard');      // → SPA (React Router)
 * redirect('/user/login/verify'); // → SPA (React Router)
 * redirect('/admin/users');       // → Drupal (full page redirect)
 */
const useRedirect = () => {
  const navigate = useNavigate();

  return useCallback(
    (path: string, options?: NavigateOptions) => {
      if (isSpaPath(path)) {
        navigate(path, options);
      } else {
        window.location.href = path;
      }
    },
    [navigate],
  );
};

export default useRedirect;
